import React from "react";

const PropertiesPanel = ({ selectedElement, onUpdateElement }) => {
  if (!selectedElement) {
    return (
      <div className="properties-panel">
        <h3>Properties</h3>
        <div className="no-selection">
          <p>Select an element to edit its properties</p>
        </div>
      </div>
    );
  }

  const handleChange = (property, value) => {
    onUpdateElement(selectedElement.id, property, value);
  };

  const formatNumber = (val) => {
    if (val === undefined || val === null) return "";
    const n = Number(val);
    if (Number.isNaN(n)) return "";
    return Number.isInteger(n) ? n : n.toFixed(1);
  };

  const isTextElement = selectedElement.type === "text";
  const isSpeechBubble = selectedElement.type === "speechBubble";
  const isShape = selectedElement.type === "shape";
  const isDrawing = selectedElement.type === "drawing";
  const isLineShape = isShape && selectedElement.shapeType === 'line';
  const showTextProperties = isSpeechBubble || isTextElement;

  const noFill = isShape && (selectedElement.fill === null || selectedElement.fill === undefined || selectedElement.fill === "transparent" || selectedElement.fill === "none");

  return (
    <div className="properties-panel">
      <h3>Properties</h3>
      
      <div className="property-group">
        <h4>Position & Size</h4>
        
        <div className="property-row">
          <label>X Position:</label>
          <input
            type="number"
            value={formatNumber(
              isDrawing
                ? (() => {
                    const pts = selectedElement.points || [];
                    if (!Array.isArray(pts) || pts.length === 0) return 0;
                    const xs = pts.map(p => p[0]);
                    return Math.min(...xs);
                  })()
                : (selectedElement.x || 0)
            )}
            onChange={(e) => handleChange("x", parseFloat(e.target.value) || 0)}
            min="0"
            step="0.1"
          />
        </div>
        
        <div className="property-row">
          <label>Y Position:</label>
          <input
            type="number"
            value={formatNumber(
              isDrawing
                ? (() => {
                    const pts = selectedElement.points || [];
                    if (!Array.isArray(pts) || pts.length === 0) return 0;
                    const ys = pts.map(p => p[1]);
                    return Math.min(...ys);
                  })()
                : (selectedElement.y || 0)
            )}
            onChange={(e) => handleChange("y", parseFloat(e.target.value) || 0)}
            min="0"
            step="0.1"
          />
        </div>
        
        {!isTextElement && (
          <>
            {!isLineShape ? (
              <>
                <div className="property-row">
                  <label>Width:</label>
                  <input
                    type="number"
                    value={formatNumber(
                      isDrawing
                        ? (() => {
                            const pts = selectedElement.points || [];
                            if (!Array.isArray(pts) || pts.length === 0) return 0;
                            const xs = pts.map(p => p[0]);
                            return Math.max(0, Math.max(...xs) - Math.min(...xs));
                          })()
                        : (selectedElement.width ?? 100)
                    )}
                    onChange={(e) => handleChange("width", parseFloat(e.target.value) || 100)}
                    min="10"
                    step="0.1"
                  />
                </div>
                
                <div className="property-row">
                  <label>Height:</label>
                  <input
                    type="number"
                    value={formatNumber(
                      isDrawing
                        ? (() => {
                            const pts = selectedElement.points || [];
                            if (!Array.isArray(pts) || pts.length === 0) return 0;
                            const ys = pts.map(p => p[1]);
                            return Math.max(0, Math.max(...ys) - Math.min(...ys));
                          })()
                        : (selectedElement.height ?? 100)
                    )}
                    onChange={(e) => handleChange("height", parseFloat(e.target.value) || 100)}
                    min="10"
                    step="0.1"
                  />
                </div>
                <div className="property-row">
                  <label>Rotation (°):</label>
                  <input
                    type="number"
                    value={formatNumber(selectedElement.rotation ?? 0)}
                    onChange={(e) => handleChange("rotation", parseFloat(e.target.value) || 0)}
                    min="-360"
                    max="360"
                    step="0.1"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="property-row">
                  <label>Length:</label>
                  <input
                    type="number"
                    value={formatNumber(selectedElement.width ?? 100)}
                    onChange={(e) => handleChange("width", parseFloat(e.target.value) || 100)}
                    min="5"
                    step="0.1"
                  />
                </div>
                <div className="property-row">
                  <label>Thickness:</label>
                  <input
                    type="number"
                    value={formatNumber(selectedElement.strokeWidth ?? 2)}
                    onChange={(e) => handleChange("strokeWidth", parseFloat(e.target.value) || 2)}
                    min="1"
                    step="0.1"
                  />
                </div>
                <div className="property-row">
                  <label>Rotation (°):</label>
                  <input
                    type="number"
                    value={formatNumber(selectedElement.rotation ?? 0)}
                    onChange={(e) => handleChange("rotation", parseFloat(e.target.value) || 0)}
                    min="-360"
                    max="360"
                    step="0.1"
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {isShape && !isLineShape && (
        <div className="property-group">
          <h4>Shape Properties</h4>
          <div className="property-row">
            <label>Fill:</label>
            <input
              type="color"
              value={noFill ? "#ffffff" : (selectedElement.fill || "#ffffff")}
              disabled={noFill}
              onChange={(e) => handleChange("fill", e.target.value)}
            />
          </div>
          <div className="property-row">
            <label></label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={noFill}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleChange("fill", null);
                  } else {
                    handleChange("fill", selectedElement.fill || "#ffffff");
                  }
                }}
              />
              No fill
            </label>
          </div>
        </div>
      )}


      {showTextProperties && (
        <div className="property-group">
          <h4>Text Properties</h4>
          
          <div className="property-row">
            <label>Text:</label>
            <textarea
              value={selectedElement.text || ""}
              onChange={(e) => handleChange("text", e.target.value)}
              rows={isTextElement ? "2" : "3"}
              placeholder={isTextElement ? "Enter text content..." : "Enter speech bubble text..."}
            />
          </div>
          
          <div className="property-row">
            <label>Font Family:</label>
            <select
              value={selectedElement.fontFamily || "Arial"}
              onChange={(e) => handleChange("fontFamily", e.target.value)}
            >
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Comic Sans MS">Comic Sans MS</option>
            </select>
          </div>
          
          <div className="property-row">
            <label>Font Size:</label>
            <input
              type="number"
              value={selectedElement.fontSize || 16}
              onChange={(e) => handleChange("fontSize", parseInt(e.target.value) || 16)}
              min="8"
              max="72"
            />
          </div>
          
          <div className="property-row">
            <label>Font Color:</label>
            <input
              type="color"
              value={selectedElement.fontColor || "#000000"}
              onChange={(e) => handleChange("fontColor", e.target.value)}
            />
          </div>
        </div>
      )}

      {isDrawing && (
        <div className="property-group">
          <h4>Stroke Properties</h4>
          
          <div className="property-row">
            <label>Stroke Width:</label>
            <input
              type="number"
              value={selectedElement.strokeWidth || 3}
              onChange={(e) => handleChange("strokeWidth", parseInt(e.target.value) || 3)}
              min="1"
              max="20"
            />
          </div>
          
          <div className="property-row">
            <label>Stroke Color:</label>
            <input
              type="color"
              value={selectedElement.strokeColor || "#000000"}
              onChange={(e) => handleChange("strokeColor", e.target.value)}
            />
          </div>
          
          <div className="property-row">
            <label>Opacity:</label>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={selectedElement.opacity || 1.0}
              onChange={(e) => handleChange("opacity", parseFloat(e.target.value))}
            />
            <span>{Math.round((selectedElement.opacity || 1.0) * 100)}%</span>
          </div>
        </div>
      )}

      <div className="property-group">
        <h4>Element Info</h4>
        <div className="property-row">
          <label>Type:</label>
          <span className="property-value">{selectedElement.type || "asset"}</span>
        </div>
        <div className="property-row">
          <label>ID:</label>
          <span className="property-value">{selectedElement.id}</span>
        </div>
      </div>
    </div>
  );
};

export default PropertiesPanel;
